/**
 * FingerprintJS Pro Vue v1.5.0 - Copyright (c) FingerprintJS, Inc, 2024 (https://fingerprintjs.com)
 * Licensed under the MIT (http://www.opensource.org/licenses/mit-license.php) license.
 */

import { FpjsClient } from '@fingerprintjs/fingerprintjs-pro-spa';
export { FingerprintJSPro, InMemoryCache, LocalStorageCache, SessionStorageCache, defaultEndpoint, defaultScriptUrlPattern, defaultTlsEndpoint } from '@fingerprintjs/fingerprintjs-pro-spa';
import { ref, inject, onMounted } from 'vue';

var version = "1.5.0";

const GET_VISITOR_DATA = Symbol('fpjs-getVisitorData');
const CLEAR_CACHE = Symbol('fpjs-clearCache');

const getOptions = (options, packageName, version) => {
    const loadOptions = {
        ...(options?.loadOptions ?? {}),
        integrationInfo: [...(options.loadOptions?.integrationInfo ?? []), `${packageName}/${version}`],
    };
    const clientOptions = {
        ...options,
        loadOptions,
    };
    return clientOptions;
};

function makeClientMethods(client) {
    const initPromise = client.init();
    const getVisitorData = async (agentOptions, ignoreCache) => {
        if (typeof window === 'undefined') {
            throw new Error('getVisitorData() can only be called in the browser. If you are using nuxt, you should apply our plugin only on client side.');
        }
        await initPromise;
        return client.getVisitorData(agentOptions, ignoreCache);
    };
    const clearCache = client.clearCache.bind(client);
    return {
        clearCache,
        getVisitorData,
    };
}

/**
 * Fingerprint Pro plugin
 *
 * @example ```ts
 * import { createApp } from 'vue';
 * import App from './App.vue';
 * import fpjsPlugin, { FpjsVueOptions } from '@fingerprintjs/fingerprintjs-pro-vue-v3';
 *
 * const app = createApp(App);
 *
 * const apiKey = '<YOUR_API_KEY>'
 *
 * app
 *   .use(fpjsPlugin, {
 *     loadOptions: {
 *       apiKey,
 *     },
 *   } as FpjsVueOptions)
 *   .mount('#app');
 * ```
 * */
const fpjsPlugin = {
    install: (app, options) => {
        const client = new FpjsClient(getOptions(options, 'fingerprintjs-pro-vue-v3', version));
        const { getVisitorData, clearCache } = makeClientMethods(client);
        app.provide(GET_VISITOR_DATA, getVisitorData);
        app.provide(CLEAR_CACHE, clearCache);
        app.config.globalProperties.$fpjs = {
            getVisitorData,
            clearCache,
        };
    },
};

function setMixinData(dataName, key, value) {
    this.$data[dataName][key] = value;
}
function createMixin(extended) {
    const suffix = extended ? 'Extended' : '';
    const dataName = extended ? 'visitorDataExtended' : 'visitorData';
    const methodName = `$getVisitorData${suffix}`;
    const getVisitorData = async function (options) {
        /**
         * We use this.$root as a fallback, because in nuxt sometimes this.$fpjs might be empty, but it might exist in $root
         * */
        const fpjs = this.$fpjs ?? this.$root?.$fpjs;
        const boundSetData = setMixinData.bind(this);
        const setData = (key, value) => {
            return boundSetData(dataName, key, value);
        };
        if (!fpjs) {
            throw new TypeError('$fpjs is not defined.');
        }
        try {
            setData('isLoading', true);
            setData('data', await fpjs.getVisitorData({
                ...(options ?? {}),
                extendedResult: extended,
            }, options?.ignoreCache));
        }
        catch (error) {
            setData('error', error);
        }
        finally {
            setData('isLoading', false);
        }
    };
    return {
        data() {
            return {
                [dataName]: {
                    isLoading: false,
                    data: undefined,
                    error: undefined,
                },
            };
        },
        methods: {
            [methodName]: getVisitorData,
        },
    };
}
/**
 * Mixin for fetching normal visitorData
 *
 * @example ```vue
 *
 * <script>
 * import { fpjsGetVisitorDataMixin } from '@fingerprintjs/fingerprintjs-pro-vue-v3';
 * //import { fpjsGetVisitorDataMixin } from '@fingerprintjs/fingerprintjs-pro-vue-v2';
 *
 * export default {
 *   // Include our mixin
 *   mixins: [fpjsGetVisitorDataMixin],
 *   async mounted() {
 *     // You can also fetch data on mount
 *     // await this.$getVisitorData();
 *   }
 * };
 * </script>
 *
 * <template>
 *   <div>
 *     <button @click='$getVisitorData'>
 *       Get visitor data
 *     </button>
 *     <span v-if='visitorData.isLoading'>
 *       Loading...
 *     </span>
 *     <span v-else-if='visitorData.isError'>
 *       Error: {{ visitorData.error }}
 *     </span>
 *     <span v-else>
 *       <!--Do something with visitorData here-->
 *     </span>
 *   </div>
 * </template>
 * ```
 * */
const fpjsGetVisitorDataMixin = createMixin(false);
/**
 * Mixin for fetching extended visitorData
 *
 * @example ```vue
 *
 * <script>
 * import { fpjsGetVisitorDataExtendedMixin } from '@fingerprintjs/fingerprintjs-pro-vue-v3';
 * //import { fpjsGetVisitorDataExtendedMixin } from '@fingerprintjs/fingerprintjs-pro-vue-v2';
 *
 * export default {
 *   // Include our mixin
 *   mixins: [fpjsGetVisitorDataExtendedMixin],
 *   async mounted() {
 *     // You can also fetch data on mount
 *     // await this.$getVisitorDataExtended();
 *   }
 * );
 * </script>
 *
 * <template>
 *   <div>
 *     <button @click='$getVisitorDataExtended'>
 *       Get visitor data
 *     </button>
 *     <span v-if='visitorDataExtended.isLoading'>
 *       Loading...
 *     </span>
 *     <span v-else-if='visitorDataExtended.isError'>
 *       Error: {{ visitorDataExtended.error }}
 *     </span>
 *     <span v-else>
 *       <!--Do something with visitorData here-->
 *     </span>
 *   </div>
 * </template>
 * ```
 * */
const fpjsGetVisitorDataExtendedMixin = createMixin(true);

/**
 * Composition API for fetching visitorData.
 *
 * @example
 * ```typescript
 *     {
 *       template: '<h1>Hello world</h1>',
 *       setup() {
 *         const { data, getData, isLoading, error } = useVisitorData({ extendedResult: true });
 *
 *         // Fetch data on mount and ignore cache
 *         // const { data, getData, isLoading, error } = useVisitorData({ extendedResult: true, ignoreCache: true }, { immediate: true });
 *       }
 *     }
 * ```
 * */
function useVisitorData({ ignoreCache: defaultIgnoreCache, ...options } = {}, { immediate = true } = {}) {
    const data = ref();
    const isLoading = ref(false);
    const currentError = ref();
    const getVisitorData = inject(GET_VISITOR_DATA);
    if (!getVisitorData) {
        throw new Error('GET_VISITOR_DATA inject data is missing, perhaps you forgot to install the plugin first?');
    }
    const getData = async (getDataOptions) => {
        isLoading.value = true;
        const ignoreCache = typeof getDataOptions?.ignoreCache === 'boolean' ? getDataOptions.ignoreCache : defaultIgnoreCache;
        try {
            const result = await getVisitorData(options, ignoreCache);
            data.value = result;
            currentError.value = undefined;
            return result;
        }
        catch (error) {
            data.value = undefined;
            if (error instanceof Error) {
                error.message = `${error.name}: ${error.message}`;
                error.name = 'FPJSAgentError';
                currentError.value = error;
            }
            return undefined;
        }
        finally {
            isLoading.value = false;
        }
    };
    onMounted(async () => {
        if (immediate) {
            await getData();
        }
    });
    return {
        getData,
        data,
        isLoading,
        error: currentError,
    };
}

export { CLEAR_CACHE, GET_VISITOR_DATA, fpjsPlugin as default, fpjsGetVisitorDataExtendedMixin, fpjsGetVisitorDataMixin, fpjsPlugin, useVisitorData };
//# sourceMappingURL=plugin.esm.js.map
